import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGet } from 'restful-react';
import { OkResponse } from '../common/interfaces';
import env from '../Env';
import { Message, Messages } from '../types/Message';
import { Project, ProjectContext } from '../types/Project';
import { UserTypeEnum, UserType } from '../types/enums/UserTypesEnum';
import { SignalREventEnum } from '../types/enums/SignalREventEnum';
import { Peer, Peers } from '../types/SignalR';
import { Encoder } from '../types/Encoder';
import { Stream } from '../types/Stream';
import useProject from './useProject';
import _ from 'lodash';

const useSignalRConnection = (context: ProjectContext) => {
  const [hubConnection, setHubConnection] = useState<HubConnection>();
  const [connectedPeers, setConnectedPeers] = useState<Peers>([]);
  const [chatMessages, setChatMessages] = useState<Messages>([]);
  const [isChatConnected, setIsChatConnected] = useState<boolean>(false);
  const [projectId, setProjectId] = useState<string>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [projectFromSignalR, setProjectFromSignalR] = useState<Project>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [encoderFromSignalR, setEncoderFromSignalR] = useState<Encoder>();

  const [streamFromSignalR, setStreamFromSignalR] = useState<Stream>();
  const [myUserId, setMyUserId] = useState<string>();
  const [myUserName, setMyUserName] = useState<string>();
  const [myPeerId, setMyPeerId] = useState<string>();
  const [myUserType, setMyUserType] = useState<UserType>(UserTypeEnum.Unknown);
  const [peerRequestingControl, setPeerRequestingControl] = useState<Peer | null>(null);
  const [myConnectionIsLive, setMyConnectionIsLive] = useState<boolean>(false);
  const [triggerRefetchChatMessages, setTriggerRefetchChatMessages] = useState<boolean>(false);
  const [isAwaitingApiSaveToComplete, setIsAwaitingApiSaveToComplete] = useState<boolean>(false);

  const hubConnectionRef = useRef(hubConnection);
  const projectIdRef = useRef(projectId);
  const myUserIdRef = useRef(myUserId);
  const myUserNameRef = useRef(myUserName);
  const myUserTypeRef = useRef(myUserType);
  const { project, setProject } = useProject();

  hubConnectionRef.current = hubConnection;
  projectIdRef.current = projectId;
  myUserIdRef.current = myUserId;
  myUserNameRef.current = myUserName;
  myUserTypeRef.current = myUserType;

  const { refetch } = useGet({
    lazy: true,
    path: '/projects/' + projectId,
    resolve: (project: OkResponse<Project>) => {
      const result = project.result;
      setChatMessages(result?.messages ?? []);
      return result;
    },
  });

  useEffect(() => {
    if (triggerRefetchChatMessages) {
      refetch();
      setTriggerRefetchChatMessages(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRefetchChatMessages]);

  const createHubConnection = async (
    projectId: string,
    userId: string,
    peerId: string,
    userType: UserType,
    userName: string,
    chatMessages: Messages,
  ) => {
    setProjectId(projectId);
    setMyUserId(userId);
    setMyUserName(userName);
    setMyUserType(userType);
    setMyPeerId(peerId);
    setChatMessages(chatMessages);

    const hubConn = new HubConnectionBuilder()
      .withUrl(`${env.baseUrl}/hub`)
      .configureLogging(LogLevel.None)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: () => {
          return 3000;
        },
      })
      .build();

    hubConn.on(SignalREventEnum.UserJoined, (peerIds: Peers) => {
      setConnectedPeers(peerIds);
    });

    hubConn.on(SignalREventEnum.MessageGroup, (newMessage: Message) => {
      setChatMessages((chatMessages) => [...chatMessages, newMessage]);
    });

    hubConn.on(SignalREventEnum.UserLeft, (peerId) => {
      setConnectedPeers((connectedPeers) =>
        connectedPeers.filter((peer) => peer.peerId !== peerId),
      );
    });

    hubConn.on(SignalREventEnum.GoLive, () => {
      setMyConnectionIsLive(true);
      setPeerRequestingControl(null);
    });

    hubConn.on(SignalREventEnum.RequestControl, (peer: Peer) => {
      setPeerRequestingControl(peer);
    });

    hubConn.on(SignalREventEnum.UpdateConnectedPeers, (peers: Peers) => {
      setConnectedPeers(peers);

      const livePeer = peers.find((x) => {
        const userTypes = [userType];

        if (userType === UserTypeEnum.Captioner) {
          userTypes.push(UserTypeEnum.AiWriter);
        }

        if (userType === UserTypeEnum.AiWriter) {
          userTypes.push(UserTypeEnum.Captioner);
        }

        return x.isCurrentlyLive && userTypes.includes(x.userType.toString() === 'Captioner' ? 1 : 0) && x.peerId !== peerId;
      });

      if (livePeer !== undefined) {
        setPeerRequestingControl(null);
        setMyConnectionIsLive(false);
      }
    });

    hubConn.on(SignalREventEnum.CallPeer, (peer: Peer) => {
      setConnectedPeers((connectedPeers) => [...connectedPeers, peer]);
    });

    hubConn.on(SignalREventEnum.UpdateProject, (data: Project) => {
      let newProject = {
        ...project,
        ...data
      };

      delete (newProject as any).updated;
      delete (newProject as any).updatedUtc;

      delete (newProject.stream as any).updated;
      delete (newProject.stream as any).updatedUtc;
      
      for(let i = 0; i < (newProject.stream.encoders?.length ?? 0); i++) {
        delete (newProject.stream.encoders?.[i] as any).updated;
        delete (newProject.stream.encoders?.[i] as any).updatedUtc;
      }

      const result = _.isEqual(newProject, context.project);

      if (!result) {
        newProject = {
          ...project,
          ...data
        };

        context.setProject(newProject);
      }
    });

    hubConn.on(SignalREventEnum.UpdateEncoder, (currentEncoder: Encoder) => {
      const newProject = {
        ...project,
        encoder: currentEncoder
      };

      setProject(newProject);
    });

    hubConn.on(SignalREventEnum.UpdateStream, (currentStream: Stream) => {
      setStreamFromSignalR(currentStream);
    });

    hubConn.on(
      SignalREventEnum.UpdateIsAwaitingApiSaveToComplete,
      (isAwaitingApiSaveToComplete: boolean) => {
        setIsAwaitingApiSaveToComplete(isAwaitingApiSaveToComplete);
      },
    );

    const startConnection = async () => {
      await hubConn.start().then(() => {
        hubConn?.invoke('JoinRoom', projectId, peerId, userType, userId, userName).catch((err) => {
          console.log(err);
        });
      });
      setHubConnection(hubConn);
      setIsChatConnected(true);
    };

    hubConn.onreconnecting(() => {
      setIsChatConnected(false);
    });

    hubConn.onreconnected(() => {
      setIsChatConnected(true);
      setTriggerRefetchChatMessages(true);
      hubConn.invoke('JoinRoom', projectId, peerId, userType, userId, userName).catch((err) => {
        console.log(err);
      });
    });

    hubConn.onclose(async () => {
      setIsChatConnected(false);
      await startConnection();
    });

    try {
      await startConnection();
    } catch (err) {
      console.log(err);
    }
  };

  const sendMessageToRoom = useCallback(
    (messageText: string) => {
      hubConnection
        ?.invoke('SendMessageToRoom', projectId, myPeerId, myUserName, messageText)
        .catch((err) => {
          console.log(err);
        });
    },
    [hubConnection, projectId, myPeerId, myUserName],
  );

  const sendRequestControl = useCallback(() => {
    hubConnection?.invoke('RequestControl', projectId).catch((err) => {
      console.log(err);
    });
  }, [hubConnection, projectId]);

  const sendTakeControl = useCallback(() => {
    hubConnection?.invoke('TakeControl', projectId, myPeerId).catch(console.log);
  }, [hubConnection, projectId, myPeerId]);

  const sendGiveControl = useCallback(() => {
    setMyConnectionIsLive(false);
    setPeerRequestingControl(null);
    hubConnection?.invoke('GiveControl', projectId, peerRequestingControl?.peerId).catch((err) => {
      console.log(err);
    });
  }, [hubConnection, projectId, peerRequestingControl]);

  const sendProjectToAllUsers = useCallback(
    (currentProject: Project) => {
      hubConnection?.invoke('UpdateProject', projectId, currentProject).catch((err) => {
        console.log(err);
      });
    },
    [hubConnection, projectId],
  );

  const sendIsAwaitingApiSaveToComplete = useCallback(
    (isAwaitingApiSaveToComplete: boolean) => {
      hubConnection
        ?.invoke('UpdateIsAwaitingApiSaveToComplete', projectId, isAwaitingApiSaveToComplete)
        .catch((err) => {
          console.log(err);
        });
    },
    [hubConnection, projectId],
  );

  const getPeerInfo = useCallback(
    (peerId: string): Peer | null => {
      if (peerId === myPeerId) {
        return {
          peerId: myPeerId,
          userId: myUserId ?? '',
          userName: myUserName ?? '',
          userType: myUserType,
          isCurrentlyLive: myConnectionIsLive,
        };
      }
      return connectedPeers.find((x) => x.peerId === peerId) ?? null;
    },
    [myPeerId, myUserId, myUserName, myUserType, myConnectionIsLive, connectedPeers],
  );

  const isUserActive = useCallback(
    (userId: string): boolean => {
      return connectedPeers.find((x) => x.userId === userId) !== undefined;
    },
    [connectedPeers],
  );

  const isUserLive = useCallback(
    (userId: string): boolean => {
      const peer: Peer | undefined = connectedPeers.find((x) => x.userId === userId);

      if (peer === undefined || peer.userType === UserTypeEnum.Producer) {
        return false;
      }

      return peer.isCurrentlyLive;
    },
    [connectedPeers],
  );

  return {
    createHubConnection,
    connectedPeers,
    isChatConnected,
    sendMessageToRoom,
    chatMessages,
    sendRequestControl,
    sendTakeControl,
    sendGiveControl,
    getPeerInfo,
    peerRequestingControl,
    sendProjectToAllUsers,
    projectFromSignalR,
    encoderFromSignalR,
    streamFromSignalR,
    myConnectionIsLive,
    isAwaitingApiSaveToComplete,
    sendIsAwaitingApiSaveToComplete,
    isUserActive,
    isUserLive,
  };
};

export default useSignalRConnection;
